import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  LIMITS,
  blankRound,
  emptyPack,
  isPlayable,
  packProgress,
  parsePack,
  pointRows,
  type CategoryDef,
  type Pack,
  type PackIssue,
  type QuestionDef,
  type RoundDef,
} from "@shared/pack";
import { samplePack } from "@shared/sample";
import { DEFAULT_THEME } from "@shared/themes";
import type { GameStateView } from "@shared/types";
import { BuiltBy, Logo } from "../components/Brand";
import { Icon } from "../components/Icon";
import { LanguagePicker, ThemePicker } from "../components/Pickers";
import {
  Button,
  IconButton,
  Input,
  Modal,
  Progress,
  Sheet,
  Textarea,
  useDialog,
} from "../components/ui";
import { apiUrl } from "../lib/api";
import { useI18n, useT } from "../lib/i18n";
import { useRoom } from "../lib/room";
import { applyTheme } from "../lib/theme";
import {
  deleteDraft,
  getDraft,
  hostTokenFor,
  listDrafts,
  listHostedRooms,
  newDraft,
  rememberHostedRoom,
  saveDraft,
  type Draft,
} from "../lib/storage";

const AUTOSAVE_MS = 400;

// ─────────────────────────────────────────────────────────────────────────────
//  The pack editor.
//
//  The first version rendered every question as a form, all at once: 71 inputs
//  and seven screens of scrolling on a phone before you could reach the last
//  one. This one shows the *board* — the same grid the players will see — and
//  opens one question at a time in a sheet. You always know what you are
//  building and how much of it is left.
//
//  Everything autosaves to this device. Export/import is the escape hatch.
// ─────────────────────────────────────────────────────────────────────────────

/** Which cell is being edited: a board cell, or the final round. */
type Selection =
  | { kind: "cell"; categoryIndex: number; questionIndex: number }
  | { kind: "wager" }
  | null;

export default function Editor() {
  const t = useT();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const dialog = useDialog();
  const { draftId } = useParams<{ draftId?: string }>();
  const [searchParams] = useSearchParams();

  // `?room=CODE` means the host opened the editor from a live room's settings.
  // In that mode the primary action replaces the running pack instead of
  // creating a new room, over the same authenticated channel the admin uses.
  const boundRoom = searchParams.get("room");
  const boundToken = boundRoom ? hostTokenFor(boundRoom) : null;
  const boundConn = useRoom(boundToken ? boundRoom! : "", "host", boundToken);

  const [draft, setDraft] = useState<Draft | null>(() => {
    if (draftId) return getDraft(draftId);
    return null; // no draft yet — show setup
  });
  const [drafts, setDrafts] = useState<Draft[]>(() => listDrafts());
  const [roundIdx, setRoundIdx] = useState(0);
  const [selection, setSelection] = useState<Selection>(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [issues, setIssues] = useState<PackIssue[] | null>(null);
  const [importError, setImportError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    applyTheme(boundRoom ? null : theme);
  }, [theme, boundRoom]);

  // Autosave, debounced so a fast typist does not hit localStorage per keystroke.
  useEffect(() => {
    if (!draft) return;
    const timer = setTimeout(() => {
      if (saveDraft(draft)) setSavedAt(Date.now());
      setDrafts(listDrafts());
    }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  const update = useCallback((mutate: (p: Pack) => Pack) => {
    setDraft((d) => (d ? { ...d, updatedAt: Date.now(), pack: mutate(d.pack) } : d));
  }, []);

  const updateRound = useCallback(
    (index: number, mutate: (r: RoundDef) => RoundDef) => {
      update((p) => ({ ...p, rounds: p.rounds.map((r, i) => (i === index ? mutate(r) : r)) }));
    },
    [update],
  );

  const openDraft = (next: Draft) => {
    setDraft(next);
    setRoundIdx(0);
    setSelection(null);
    setIssues(null);
    setImportError(false);
    setShowLibrary(false);
    navigate(`/edit/${next.id}`, { replace: true });
  };

  const startFromPack = (pack: Pack) => {
    const d = newDraft(pack);
    saveDraft(d);
    openDraft(d);
  };

  // ───────────────────────── import / export ─────────────────────────

  const exportPack = () => {
    if (!draft) return;
    const blob = new Blob([JSON.stringify(draft.pack, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${draft.pack.title.replace(/[^\w-]+/g, "_").slice(0, 40) || "osj-pack"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importPack = async (file: File) => {
    setImportError(false);
    setIssues(null);
    try {
      const parsed = parsePack(JSON.parse(await file.text()));
      if (!isPlayable(parsed.pack)) {
        setImportError(true);
        return;
      }
      startFromPack(parsed.pack);
      // Show what had to be repaired — a hand-edited file must never be
      // silently altered.
      if (parsed.issues.length) setIssues(parsed.issues);
    } catch {
      setImportError(true);
    }
  };

  // ───────────────────── create room / save to room ─────────────────────

  const createRoom = async () => {
    if (!draft || !isPlayable(draft.pack) || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await fetch(apiUrl("/api/rooms"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pack: draft.pack, theme, language: lang }),
      });
      const data = (await res.json()) as { code?: string; hostToken?: string; error?: string };
      if (!res.ok || !data.code || !data.hostToken) {
        setCreateError(data.error ?? "createFailed");
        return;
      }
      rememberHostedRoom({
        code: data.code,
        hostToken: data.hostToken,
        title: draft.pack.title,
        createdAt: Date.now(),
      });
      navigate(`/r/${data.code}/admin`);
    } catch {
      setCreateError("network");
    } finally {
      setCreating(false);
    }
  };

  // Send, then leave only once the room has echoed a fresh snapshot back: a
  // WebSocket closed the same tick as a send can drop the frame, and silently
  // losing rewritten questions is the worst bug this screen could have.
  const [savePending, setSavePending] = useState(false);
  const stateAtSend = useRef<GameStateView | null>(null);

  const saveToRoom = () => {
    if (!draft || !boundRoom || !isPlayable(draft.pack) || savePending) return;
    stateAtSend.current = boundConn.state;
    setSavePending(true);
    boundConn.send({ t: "host:setPack", pack: draft.pack });
    const known = listHostedRooms().find((r) => r.code === boundRoom);
    if (known) rememberHostedRoom({ ...known, title: draft.pack.title });
  };

  useEffect(() => {
    if (!savePending || !boundRoom) return;
    if (boundConn.state && boundConn.state !== stateAtSend.current) {
      navigate(`/r/${boundRoom}/admin`);
      return;
    }
    const timer = setTimeout(() => navigate(`/r/${boundRoom}/admin`), 3000);
    return () => clearTimeout(timer);
  }, [savePending, boundConn.state, boundRoom, navigate]);

  // ───────────────────────── setup (no draft yet) ─────────────────────────

  if (!draft) {
    return (
      <SetupScreen
        drafts={drafts}
        onCreate={startFromPack}
        onOpen={openDraft}
        onLoadSample={() => startFromPack(samplePack(lang))}
        onImport={() => fileInputRef.current?.click()}
        fileInput={
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void importPack(file);
              e.target.value = "";
            }}
          />
        }
        importError={importError}
      />
    );
  }

  const pack = draft.pack;
  const round: RoundDef | undefined = pack.rounds[roundIdx];
  const progress = packProgress(pack);
  const playable = isPlayable(pack);

  const selectedQuestion: QuestionDef | null =
    selection?.kind === "cell"
      ? (round?.categories[selection.categoryIndex]?.questions[selection.questionIndex] ?? null)
      : null;

  return (
    <div className="min-h-full flex flex-col">
      {/* ───── header ───── */}
      <header className="shrink-0 border-b border-line">
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5">
          <Logo size={24} showWordmark={false} />
          {boundRoom && (
            <Link
              to={`/r/${boundRoom}/admin`}
              className="inline-flex items-center gap-1 h-8 px-2 rounded-control bg-surface2 hover:bg-ink/[0.14] text-xs transition-colors shrink-0"
            >
              <Icon name="arrowLeft" />
              <span className="hidden sm:inline">{boundRoom}</span>
            </Link>
          )}
          <Input
            value={pack.title}
            maxLength={LIMITS.title}
            aria-label={t("gameTitle")}
            onChange={(e) => update((p) => ({ ...p, title: e.target.value }))}
            className="type-display !text-base sm:!text-lg h-9 flex-1 min-w-0 bg-transparent border-transparent hover:border-line focus:bg-input"
          />
          {savedAt && (
            <Icon name="check" className="text-ink/25 shrink-0 hidden sm:block" />
          )}
          <IconButton
            size="sm"
            icon="grid"
            label={t("savedDrafts")}
            onClick={() => setShowLibrary(true)}
          />
          <IconButton
            size="sm"
            icon="settings"
            label={t("settings")}
            onClick={() => setShowSettings(true)}
          />
        </div>

        {/* round tabs — scroll rather than wrap, so the board stays put */}
        <div className="flex items-center gap-1.5 px-3 sm:px-4 pb-2 overflow-x-auto">
          {pack.rounds.map((r, i) => (
            <button
              key={i}
              onClick={() => {
                setRoundIdx(i);
                setSelection(null);
              }}
              className={`h-8 px-3 rounded-control text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                i === roundIdx
                  ? "bg-accent text-onAccent"
                  : "bg-surface2 text-ink/65 hover:text-ink"
              }`}
            >
              {r.name || `${t("round")} ${i + 1}`}
            </button>
          ))}
          {pack.rounds.length < LIMITS.rounds && (
            <IconButton
              size="sm"
              icon="plus"
              label={t("addRound")}
              className="shrink-0 border border-dashed border-line"
              onClick={() => {
                const n = pack.rounds.length;
                const prev = pack.rounds[n - 1];
                update((p) => ({
                  ...p,
                  rounds: [
                    ...p.rounds,
                    blankRound(
                      `${t("round")} ${n + 1}`,
                      t("category"),
                      prev ? pointRows(prev).length : 5,
                      prev ? prev.categories.length : 3,
                      // Round 2 traditionally doubles; follow the previous ladder.
                      prev ? (pointRows(prev)[0] ?? 100) * 2 : 100,
                    ),
                  ],
                }));
                setRoundIdx(n);
              }}
            />
          )}
        </div>
      </header>

      {/* ───── the board ───── */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        {round ? (
          <BoardEditor
            round={round}
            selection={selection}
            onSelect={setSelection}
            onChange={(mutate) => updateRound(roundIdx, mutate)}
            onRemoveRound={async () => {
              const ok = await dialog.confirm({
                title: t("removeRound"),
                body: t("removeRoundConfirm"),
                confirmLabel: t("removeRound"),
                cancelLabel: t("cancel"),
                destructive: true,
              });
              if (!ok) return;
              update((p) => ({ ...p, rounds: p.rounds.filter((_, i) => i !== roundIdx) }));
              setRoundIdx((i) => Math.max(0, i - 1));
              setSelection(null);
            }}
          />
        ) : (
          <div className="p-8 text-center text-ink/45">{t("noRoundsYet")}</div>
        )}

        <WagerCard pack={pack} update={update} onOpen={() => setSelection({ kind: "wager" })} />

        <div className="px-4 py-6">
          <BuiltBy />
        </div>
      </main>

      {/* ───── sticky action bar ───── */}
      {/*
        Stacks on a phone: the meter and a long localised button label do not
        fit side by side at 375px, and a squeezed primary action is worse than
        a taller bar.
      */}
      <div
        className="shrink-0 border-t border-line px-3 sm:px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3"
        style={{ background: "var(--c-panel)" }}
      >
        <Progress value={progress.written} max={progress.total} label={t("questionsWritten")} />
        <div className="hidden sm:block flex-1" />
        {createError && <span className="text-danger text-xs">{createError}</span>}
        {boundRoom && boundToken ? (
          <Button
            variant="primary"
            size="lg"
            iconRight="arrowRight"
            block
            className="sm:w-auto"
            onClick={saveToRoom}
            disabled={!playable || boundConn.status !== "open" || savePending}
          >
            {savePending ? "…" : t("saveToRoom")}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="lg"
            iconRight="arrowRight"
            block
            className="sm:w-auto"
            onClick={createRoom}
            disabled={!playable || creating}
          >
            {creating ? "…" : t("startHosting")}
          </Button>
        )}
      </div>

      {/* ───── question sheet ───── */}
      <Sheet
        open={selection !== null}
        onClose={() => setSelection(null)}
        title={
          selection?.kind === "wager" ? (
            <span className="type-display text-lg">{t("finalRound")}</span>
          ) : selection?.kind === "cell" && round ? (
            <span className="flex items-baseline gap-2 min-w-0">
              <span className="type-display text-lg truncate">
                {round.categories[selection.categoryIndex]?.category}
              </span>
              <span className="type-code text-accentLight shrink-0">
                {selectedQuestion?.points}
              </span>
            </span>
          ) : null
        }
      >
        {selection?.kind === "cell" && selectedQuestion && (
          <QuestionForm
            question={selectedQuestion}
            onChange={(patch) =>
              updateRound(roundIdx, (r) => ({
                ...r,
                categories: r.categories.map((c, ci) =>
                  ci !== selection.categoryIndex
                    ? c
                    : {
                        ...c,
                        questions: c.questions.map((q, qi) =>
                          qi === selection.questionIndex ? { ...q, ...patch } : q,
                        ),
                      },
                ),
              }))
            }
          />
        )}
        {selection?.kind === "wager" && pack.wager && (
          <QuestionForm
            question={{
              points: 0,
              q: pack.wager.question,
              a: pack.wager.answer,
              image: pack.wager.image ?? null,
            }}
            hidePoints
            onChange={(patch) =>
              update((p) => ({
                ...p,
                wager: {
                  ...p.wager!,
                  ...(patch.q !== undefined ? { question: patch.q } : {}),
                  ...(patch.a !== undefined ? { answer: patch.a } : {}),
                  ...(patch.image !== undefined ? { image: patch.image } : {}),
                },
              }))
            }
          />
        )}
      </Sheet>

      <LibraryModal
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        drafts={drafts}
        currentId={draft.id}
        onOpen={openDraft}
        onNew={() => {
          setDraft(null);
          setShowLibrary(false);
          navigate("/new", { replace: true });
        }}
        onDuplicate={() => {
          const copy = newDraft(JSON.parse(JSON.stringify(pack)) as Pack);
          copy.pack.title = `${pack.title} (copy)`;
          startFromPack(copy.pack);
        }}
        onDelete={async (d) => {
          const ok = await dialog.confirm({
            title: t("delete"),
            body: `${d.pack.title} — ${t("deleteDraftConfirm")}`,
            confirmLabel: t("delete"),
            cancelLabel: t("cancel"),
            destructive: true,
          });
          if (!ok) return;
          deleteDraft(d.id);
          const remaining = listDrafts();
          setDrafts(remaining);
          if (d.id === draft.id) {
            if (remaining[0]) openDraft(remaining[0]);
            else {
              setDraft(null);
              navigate("/new", { replace: true });
            }
          }
        }}
        onImport={() => fileInputRef.current?.click()}
        onExport={exportPack}
        importError={importError}
        issues={issues}
      />

      <Modal open={showSettings} onClose={() => setShowSettings(false)} title={t("settings")}>
        <div className="flex flex-col gap-5">
          <div>
            <div className="type-eyebrow text-ink/45 mb-2">{t("language")}</div>
            <LanguagePicker />
          </div>
          {!boundRoom && <ThemePicker value={theme} onChange={setTheme} />}
          {round && (
            <div>
              <div className="type-eyebrow text-ink/45 mb-2">{t("roundName")}</div>
              <Input
                value={round.name}
                maxLength={LIMITS.categoryName}
                onChange={(e) => updateRound(roundIdx, (r) => ({ ...r, name: e.target.value }))}
              />
            </div>
          )}
        </div>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void importPack(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Setup — the one screen a brand-new pack starts on
// ─────────────────────────────────────────────────────────────────────────────

const LADDERS = [100, 200, 500] as const;

function SetupScreen({
  drafts,
  onCreate,
  onOpen,
  onLoadSample,
  onImport,
  fileInput,
  importError,
}: {
  drafts: Draft[];
  onCreate: (pack: Pack) => void;
  onOpen: (d: Draft) => void;
  onLoadSample: () => void;
  onImport: () => void;
  fileInput: React.ReactNode;
  importError: boolean;
}) {
  const t = useT();
  const { lang } = useI18n();
  const [title, setTitle] = useState(lang === "de" ? "Mein Quiz" : "My quiz");
  const [categories, setCategories] = useState(3);
  const [rows, setRows] = useState(5);
  const [step, setStep] = useState<number>(100);
  const [rounds, setRounds] = useState(2);
  const [wager, setWager] = useState(true);

  useEffect(() => {
    applyTheme(null);
  }, []);

  const create = () => {
    const pack = emptyPack(title.trim() || "OSJ");
    pack.rounds = Array.from({ length: rounds }, (_, i) =>
      // Each round doubles the ladder, the way the format traditionally works.
      blankRound(`${t("round")} ${i + 1}`, t("category"), rows, categories, step * 2 ** i),
    );
    if (wager) pack.wager = { name: t("finalRound"), question: "", answer: "", image: null };
    onCreate(pack);
  };

  const total = rounds * categories * rows + (wager ? 1 : 0);

  return (
    <div className="min-h-full flex flex-col">
      <header className="flex items-center gap-3 px-5 py-4">
        <Logo size={28} />
        <div className="flex-1" />
        <LanguagePicker compact />
      </header>

      <main className="flex-1 flex flex-col items-center px-5 pb-12">
        <div className="w-full max-w-lg flex flex-col gap-7 pt-4 sm:pt-8">
          <div>
            <h1 className="type-display text-3xl sm:text-4xl text-accentLight">
              {t("setupTitle")}
            </h1>
            <p className="text-ink/50 mt-2 leading-relaxed">{t("setupIntro")}</p>
          </div>

          <div>
            <label className="block type-eyebrow text-ink/45 mb-1.5">{t("gameTitle")}</label>
            <Input
              value={title}
              maxLength={LIMITS.title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              className="type-display !text-xl h-12"
            />
          </div>

          <Stepper
            label={t("setupRounds")}
            value={rounds}
            min={1}
            max={LIMITS.rounds}
            onChange={setRounds}
          />
          <Stepper
            label={t("setupCategories")}
            value={categories}
            min={1}
            max={LIMITS.categoriesPerRound}
            onChange={setCategories}
          />
          <Stepper
            label={t("setupRows")}
            value={rows}
            min={1}
            max={LIMITS.questionsPerCategory}
            onChange={setRows}
          />

          <div>
            <div className="type-eyebrow text-ink/45 mb-2">{t("setupLadder")}</div>
            <div className="flex flex-wrap gap-2">
              {LADDERS.map((l) => (
                <Button
                  key={l}
                  size="sm"
                  variant={step === l ? "primary" : "secondary"}
                  aria-pressed={step === l}
                  onClick={() => setStep(l)}
                >
                  <span className="tabular">
                    {l}–{l * rows}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={wager}
              onChange={(e) => setWager(e.target.checked)}
              className="w-4 h-4 accent-current"
            />
            <span>{t("enableFinal")}</span>
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" size="xl" iconRight="arrowRight" onClick={create}>
              {t("setupCreate")}
            </Button>
            <span className="text-ink/40 text-sm tabular">
              {t("setupTotal", { n: total })}
            </span>
          </div>

          <div className="border-t border-line pt-6 flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              <Button icon="sparkles" onClick={onLoadSample}>
                {t("loadSample")}
              </Button>
              <Button icon="upload" onClick={onImport}>
                {t("importPack")}
              </Button>
            </div>
            {importError && <p className="text-danger text-sm">{t("importFailed")}</p>}

            {drafts.length > 0 && (
              <div className="mt-2">
                <h2 className="type-eyebrow text-ink/45 mb-2">{t("savedDrafts")}</h2>
                <ul className="flex flex-col gap-1">
                  {drafts.slice(0, 6).map((d) => (
                    <li key={d.id}>
                      <button
                        onClick={() => onOpen(d)}
                        className="w-full text-left h-10 px-3 rounded-control bg-surface1 hover:bg-surface2 transition-colors flex items-center gap-2"
                      >
                        <Icon name="grid" className="text-ink/35 shrink-0" />
                        <span className="truncate">{d.pack.title || "—"}</span>
                        <span className="ml-auto text-xs tabular text-ink/35 shrink-0">
                          {packProgress(d.pack).written}/{packProgress(d.pack).total}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </main>
      {fileInput}
    </div>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex-1 text-ink/70">{label}</span>
      <div className="flex items-center gap-1">
        <IconButton
          size="sm"
          variant="secondary"
          icon="minus"
          label={`${label} −`}
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
        />
        <span className="w-10 text-center type-display text-xl tabular">{value}</span>
        <IconButton
          size="sm"
          variant="secondary"
          icon="plus"
          label={`${label} +`}
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  The board — the editor's main surface
// ─────────────────────────────────────────────────────────────────────────────

function BoardEditor({
  round,
  selection,
  onSelect,
  onChange,
  onRemoveRound,
}: {
  round: RoundDef;
  selection: Selection;
  onSelect: (s: Selection) => void;
  onChange: (mutate: (r: RoundDef) => RoundDef) => void;
  onRemoveRound: () => void;
}) {
  const t = useT();
  const rows = pointRows(round);
  const canAddCategory = round.categories.length < LIMITS.categoriesPerRound;

  return (
    <div className="p-3 sm:p-4">
      {/*
        Columns get a minimum width and the board scrolls sideways past it.
        Six categories squeezed into a 375px screen would be unreadable
        slivers, which is what the previous `1fr` grid did.
      */}
      <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4 pb-1">
        <div
          className="grid gap-2 min-w-max"
          style={{
            gridTemplateColumns: `repeat(${round.categories.length}, minmax(9.5rem, 1fr))`,
          }}
        >
          {/* category headers */}
          {round.categories.map((cat, ci) => (
            <CategoryHeader
              key={ci}
              category={cat}
              onRename={(name) =>
                onChange((r) => ({
                  ...r,
                  categories: r.categories.map((c, i) => (i === ci ? { ...c, category: name } : c)),
                }))
              }
              onRemove={() =>
                onChange((r) => ({ ...r, categories: r.categories.filter((_, i) => i !== ci) }))
              }
            />
          ))}
          {/* cells, row by row */}
          {rows.map((points) =>
            round.categories.map((cat, ci) => {
              const qi = cat.questions.findIndex((q) => q.points === points);
              const q = qi >= 0 ? cat.questions[qi] : null;
              const active =
                selection?.kind === "cell" &&
                selection.categoryIndex === ci &&
                selection.questionIndex === qi;
              const written = Boolean(q?.q.trim());

              if (!q) {
                return (
                  <button
                    key={`${points}-${ci}`}
                    onClick={() =>
                      onChange((r) => ({
                        ...r,
                        categories: r.categories.map((c, i) =>
                          i !== ci
                            ? c
                            : {
                                ...c,
                                questions: [...c.questions, { points, q: "", a: "", image: null }],
                              },
                        ),
                      }))
                    }
                    className="min-h-[3.75rem] rounded-card border-2 border-dashed border-line hover:bg-surface1 text-ink/25 transition-colors flex items-center justify-center"
                  >
                    <Icon name="plus" />
                  </button>
                );
              }

              return (
                <button
                  key={`${points}-${ci}`}
                  onClick={() => onSelect({ kind: "cell", categoryIndex: ci, questionIndex: qi })}
                  aria-label={`${cat.category} ${points}`}
                  className={`relative min-h-[3.75rem] rounded-card px-2 py-2 transition-all duration-100 flex flex-col items-center justify-center gap-1 ${
                    active
                      ? "ring-2 ring-accentLight bg-boardDark"
                      : written
                        ? "bg-board shadow-tile hover:brightness-110"
                        : "bg-board/25 hover:bg-board/40"
                  }`}
                >
                  <span
                    className={`type-display text-xl sm:text-2xl tabular ${
                      written ? "text-onBoard" : "text-ink/35"
                    }`}
                  >
                    {points}
                  </span>
                  {/* A one-line preview turns the grid into a map of what is
                      written, not just a set of buttons. */}
                  {written ? (
                    <span className="text-[0.65rem] leading-tight text-ink/50 line-clamp-2 text-center px-0.5">
                      {q.q}
                    </span>
                  ) : (
                    <span className="text-[0.65rem] text-ink/25">{t("cellEmpty")}</span>
                  )}
                </button>
              );
            }),
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {canAddCategory && (
          <Button
            size="sm"
            variant="ghost"
            icon="plus"
            className="border border-dashed border-line"
            onClick={() =>
              onChange((r) => ({
                ...r,
                categories: [
                  ...r.categories,
                  {
                    category: `${t("category")} ${r.categories.length + 1}`,
                    questions: rows.map((points) => ({ points, q: "", a: "", image: null })),
                  },
                ],
              }))
            }
          >
            {t("addCategory")}
          </Button>
        )}
        {rows.length < LIMITS.questionsPerCategory && (
          <Button
            size="sm"
            variant="ghost"
            icon="plus"
            className="border border-dashed border-line"
            onClick={() =>
              onChange((r) => {
                const existing = pointRows(r);
                const stepSize =
                  existing.length >= 2 ? existing[1] - existing[0] : existing[0] || 100;
                const next = (existing[existing.length - 1] ?? 0) + (stepSize || 100);
                return {
                  ...r,
                  categories: r.categories.map((c) => ({
                    ...c,
                    questions: [...c.questions, { points: next, q: "", a: "", image: null }],
                  })),
                };
              })
            }
          >
            {t("addRow")}
          </Button>
        )}
        <div className="flex-1" />
        <Button
          size="sm"
          variant="ghost"
          icon="trash"
          className="text-danger/70"
          onClick={onRemoveRound}
        >
          {t("removeRound")}
        </Button>
      </div>
    </div>
  );
}

function CategoryHeader({
  category,
  onRename,
  onRemove,
}: {
  category: CategoryDef;
  onRename: (name: string) => void;
  onRemove: () => void;
}) {
  const t = useT();
  return (
    <div className="relative group">
      <input
        value={category.category}
        maxLength={LIMITS.categoryName}
        onChange={(e) => onRename(e.target.value)}
        placeholder={t("category")}
        aria-label={t("category")}
        className="w-full h-14 bg-boardDark border border-line rounded-card px-2 pr-7 type-display text-center uppercase text-[0.7rem] sm:text-xs leading-tight focus:outline-none focus:border-accentLight focus:ring-2 focus:ring-accentLight/40"
      />
      <IconButton
        size="sm"
        icon="x"
        label={t("removeCategory")}
        className="absolute top-1 right-0.5 h-6 w-6 opacity-0 group-hover:opacity-100 focus:opacity-100 text-ink/40 hover:text-danger"
        onClick={onRemove}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  One question, in the sheet
// ─────────────────────────────────────────────────────────────────────────────

function QuestionForm({
  question,
  hidePoints = false,
  onChange,
}: {
  question: QuestionDef;
  hidePoints?: boolean;
  onChange: (patch: Partial<QuestionDef>) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-4">
      {!hidePoints && (
        <div>
          <label className="block type-eyebrow text-ink/45 mb-1.5">{t("pointsLabel")}</label>
          <Input
            type="number"
            value={question.points}
            onChange={(e) => onChange({ points: Number(e.target.value) })}
            className="w-28 type-display text-center tabular h-11"
          />
        </div>
      )}

      <div>
        <label className="block type-eyebrow text-ink/45 mb-1.5">{t("questionLabel")}</label>
        <Textarea
          value={question.q}
          maxLength={LIMITS.questionText}
          rows={4}
          autoFocus
          onChange={(e) => onChange({ q: e.target.value })}
          className="text-base"
        />
      </div>

      <div>
        <label className="block type-eyebrow text-ink/45 mb-1.5">{t("answerLabel")}</label>
        <Textarea
          value={question.a}
          maxLength={LIMITS.answerText}
          rows={2}
          onChange={(e) => onChange({ a: e.target.value })}
          className="text-base text-accentLight"
        />
      </div>

      <div>
        <label className="block type-eyebrow text-ink/45 mb-1.5">{t("imageLabel")}</label>
        <Input
          value={question.image ?? ""}
          maxLength={LIMITS.imageUrl}
          inputMode="url"
          placeholder="https://…"
          onChange={(e) => onChange({ image: e.target.value || null })}
          className="text-sm"
        />
        {question.image && (
          <img
            src={question.image}
            alt=""
            className="mt-2 max-h-40 w-auto rounded-card object-contain mx-auto"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Final round + library
// ─────────────────────────────────────────────────────────────────────────────

function WagerCard({
  pack,
  update,
  onOpen,
}: {
  pack: Pack;
  update: (mutate: (p: Pack) => Pack) => void;
  onOpen: () => void;
}) {
  const t = useT();
  const w = pack.wager;

  return (
    <div className="px-3 sm:px-4 pb-2">
      <div className="rounded-card border border-line bg-surface1 p-3 flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={w !== null}
            onChange={(e) =>
              update((p) => ({
                ...p,
                wager: e.target.checked
                  ? { name: t("finalRound"), question: "", answer: "", image: null }
                  : null,
              }))
            }
            className="w-4 h-4 accent-current"
          />
          <span className="type-display">{t("finalRound")}</span>
        </label>
        <div className="flex-1" />
        {w && (
          <>
            <span className="text-sm text-ink/45 truncate max-w-[14rem]">
              {w.question || t("cellEmpty")}
            </span>
            <Button size="sm" icon="pencil" onClick={onOpen}>
              {t("edit")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function LibraryModal({
  open,
  onClose,
  drafts,
  currentId,
  onOpen,
  onNew,
  onDuplicate,
  onDelete,
  onImport,
  onExport,
  importError,
  issues,
}: {
  open: boolean;
  onClose: () => void;
  drafts: Draft[];
  currentId: string;
  onOpen: (d: Draft) => void;
  onNew: () => void;
  onDuplicate: () => void;
  onDelete: (d: Draft) => void;
  onImport: () => void;
  onExport: () => void;
  importError: boolean;
  issues: PackIssue[] | null;
}) {
  const t = useT();
  return (
    <Modal open={open} onClose={onClose} title={t("savedDrafts")}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon="filePlus" onClick={onNew}>
            {t("newDraft")}
          </Button>
          <Button size="sm" icon="copy" onClick={onDuplicate}>
            {t("duplicateDraft")}
          </Button>
          <Button size="sm" icon="upload" onClick={onImport}>
            {t("importPack")}
          </Button>
          <Button size="sm" icon="download" onClick={onExport}>
            {t("exportPack")}
          </Button>
        </div>

        {importError && <p className="text-danger text-sm">{t("importFailed")}</p>}
        {issues && issues.length > 0 && (
          <div className="text-warning text-xs bg-warning/10 border border-warning/25 rounded-card p-3">
            <p className="font-semibold mb-1.5">{t("importRepaired")}</p>
            <ul className="space-y-1">
              {issues.slice(0, 6).map((iss, i) => (
                <li key={i} className="leading-snug">
                  <span className="opacity-55">{iss.path}</span> — {iss.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="flex flex-col gap-1 max-h-72 overflow-y-auto">
          {drafts.map((d) => {
            const p = packProgress(d.pack);
            return (
              <li key={d.id} className="group flex items-center gap-1">
                <button
                  onClick={() => onOpen(d)}
                  className={`flex-1 min-w-0 text-left h-11 px-3 rounded-control flex items-center gap-2 transition-colors ${
                    d.id === currentId
                      ? "bg-accent text-onAccent font-semibold"
                      : "hover:bg-surface2"
                  }`}
                >
                  <span className="truncate">{d.pack.title || "—"}</span>
                  <span className="ml-auto text-xs tabular opacity-60 shrink-0">
                    {p.written}/{p.total}
                  </span>
                </button>
                <IconButton
                  size="sm"
                  icon="trash"
                  label={t("delete")}
                  className="text-ink/30 hover:text-danger"
                  onClick={() => onDelete(d)}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
